class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    /* ZUCK 2026-10-05: a extensao de app `vs-toolkit` (gifter-render.js) reescreve a classe
       `is-empty` da raiz a partir de um retrato de carrinho que pode estar velho. Medido no live
       em 2026-10-05, 2 de 3 rodadas: ela devolve `is-empty` ~280ms depois do add-to-cart, com o
       item ja no DOM e `item_count` 1 no /cart.js. A CSS de estado vazio entao esconde o
       cabecalho, o X de fechar e a linha do item, e o `transitionend` do open() abaixo quebra num
       TypeError. O app esta fora do alcance deste tema, entao a gaveta defende a propria classe:
       o observador devolve `is-empty` ao que o markup servido diz. Tirar a classe nao dispara
       acao nova, logo nao existe laco. */
    this.zuckObservadorVazio = new MutationObserver(() => this.zuckCorrigirEstadoVazio());
    this.zuckObservadorVazio.observe(this, { attributes: true, attributeFilter: ['class'], subtree: true });
  }

  /* ZUCK 2026-10-05: verdade do terreno = o markup que a secao `cart-drawer` acabou de renderizar.
     `.cart-item` so existe com item dentro; `.drawer__inner-empty` so existe com carrinho vazio.
     Devolve true (vazio), false (com item) ou null (indeterminado — e ai nao mexe em nada). */
  zuckEstaVazioPeloMarkup() {
    if (this.querySelector('.cart-item')) return false;
    if (this.querySelector('.drawer__inner-empty')) return true;
    return null;
  }

  /* ZUCK 2026-10-05: alinha `is-empty` com o markup. A CSS de estado vazio dispara por
     DESCENDENCIA — `.is-empty .cart__contents { display: none }` em assets/component-cart.css —
     entao a classe errada em QUALQUER portador dentro da gaveta esconde a linha do item. A
     extensao escreve em dois: a raiz e o `.drawer__inner`. Medido em 2026-10-05: raiz suja
     esconde cabecalho + X + item; `.drawer__inner` sujo esconde so o item (1 de 6 rodadas).
     TIRAR a classe errada vale para todo portador — e so conserto de defeito.
     POR a classe fica restrito a raiz, que e o unico portador que o proprio tema ja escreve
     (assets/cart.js: `cartDrawerWrapper.classList.toggle('is-empty', item_count === 0)`); nao
     invento estado vazio em `cart-drawer-items`, que esconderia o rodape de Finalizar. */
  zuckCorrigirEstadoVazio() {
    const vazio = this.zuckEstaVazioPeloMarkup();
    if (vazio === null) return;
    if (vazio === false) {
      const portadores = [this, this.querySelector('.drawer__inner'), this.querySelector('cart-drawer-items')];
      for (const el of portadores) if (el && el.classList.contains('is-empty')) el.classList.remove('is-empty');
      return;
    }
    if (!this.classList.contains('is-empty')) this.classList.add('is-empty');
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        /* ZUCK 2026-10-05: o container do foco sai do MARKUP, nao da classe. Com classe e markup
           em desacordo, `.drawer__inner-empty` vinha null e o getFocusableElements de
           assets/global.js nao tem guarda de nulo — era exatamente este o
           `TypeError: Cannot read properties of null (reading 'querySelectorAll')` do
           add-to-cart. O `|| document.getElementById('CartDrawer') || this` fecha a porta. */
        this.zuckCorrigirEstadoVazio();
        const containerToTrapFocusOn =
          (this.zuckEstaVazioPeloMarkup() === true ? this.querySelector('.drawer__inner-empty') : null) ||
          document.getElementById('CartDrawer') ||
          this;
        const focusElement =
          this.querySelector('.drawer__inner') || this.querySelector('.drawer__close') || containerToTrapFocusOn;
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const cartRecModal = document.getElementById('CartRecQuickAdd');
    if (cartRecModal?.hasAttribute('open') && typeof cartRecModal.hide === 'function') {
      cartRecModal.hide(true);
    }

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      /* ZUCK 2026-10-05: recalcula o estado vazio a partir do HTML de secao JA aplicado, antes do
         open(). Assim a gaveta nao depende de quem a chamou limpar a classe — hoje o
         assets/product-form.js limpa no `.finally()` e o script embutido da PDP limpa na mao. */
      this.zuckCorrigirEstadoVazio();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
